import { supabase } from '@/integrations/supabase/client';

export type ClientErrorType =
  | 'hooks_mismatch'
  | 'chunk_load'
  | 'runtime'
  | 'unhandled_promise';

interface ReportArgs {
  type: ClientErrorType;
  error: unknown;
  componentStack?: string | null;
  autoReloaded?: boolean;
}

function detectAppMode(pathname: string): string {
  if (pathname.includes('/oposiciones-c1')) return 'oposiciones_c1';
  if (pathname.includes('/oposiciones-c2')) return 'oposiciones_c2';
  if (pathname.includes('/oposiciones-iipp')) return 'oposiciones_iipp';
  if (pathname.includes('/guardia-civil')) return 'oposiciones_guardia_civil';
  if (pathname.includes('/oposiciones')) return 'oposiciones';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/auth')) return 'auth';
  if (pathname.startsWith('/app')) return 'funcionarios';
  return 'public';
}

function safeMessage(error: unknown): string {
  if (error instanceof Error) return error.message || 'Error sin mensaje';
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error).slice(0, 1000);
  } catch {
    return 'Error no serializable';
  }
}

function safeStack(error: unknown): string | null {
  if (error instanceof Error && error.stack) return error.stack.slice(0, 8000);
  return null;
}

/**
 * Reporta un error del cliente a la tabla `client_error_logs`.
 * Best-effort: nunca lanza, nunca bloquea.
 */
export async function reportClientError({
  type,
  error,
  componentStack,
  autoReloaded = false,
}: ReportArgs): Promise<void> {
  try {
    let userId: string | null = null;
    let userEmail: string | null = null;

    try {
      const { data } = await supabase.auth.getUser();
      userId = data.user?.id ?? null;
      userEmail = data.user?.email ?? null;
    } catch {
      // sin sesión: ok
    }

    const route = typeof window !== 'undefined' ? window.location.pathname : null;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    await supabase.from('client_error_logs').insert({
      user_id: userId,
      user_email: userEmail,
      error_type: type,
      message: safeMessage(error).slice(0, 2000),
      component_stack: componentStack ? componentStack.slice(0, 8000) : null,
      error_stack: safeStack(error),
      route,
      user_agent: ua,
      auto_reloaded: autoReloaded,
      app_mode: route ? detectAppMode(route) : null,
    });
  } catch (e) {
    // Silencioso: no queremos que el reporter cause más errores
    console.warn('[errorReporting] no se pudo registrar el error:', e);
  }
}
